import clsx from "clsx";
import { useCallback, useMemo } from "react";
import CountUp from "react-countup";
import {
  MdArrowDownward,
  MdOutlineArrowUpward,
  MdOutlineSavings,
} from "react-icons/md";
import { ICON_SIZES } from "src/constants/icons";
import {
  COUNT_UP_ANIMATION_DURATION,
  ETransactionType,
} from "src/helpers/constants";
import { formatCurrency } from "src/helpers/utils";
import useColors from "src/hooks/useColors";
import useUiPreferences from "src/store/useUiPreferences";

interface ICurrencyBadge {
  kind: ETransactionType | "DIFFERENCE" | "NONE";
  value: number;
  currencySymbol?: string;
  showIcon?: boolean;
}

const CurrencyBadge: React.FC<ICurrencyBadge> = ({
  kind,
  value,
  currencySymbol = "",
  showIcon = true,
}) => {
  const enableAnimations = useUiPreferences(
    (state) => state.misc.enableComplexAnimations,
  );
  const { getIncomeColors, getExpensesColors, getBalanceColors } = useColors();

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  const isPositiveDifference = useMemo(() => {
    return value >= 0;
  }, [value]);

  const getBackgroundStyles = useMemo(() => {
    switch (kind) {
      case ETransactionType.INCOME: {
        return getIncomeColors().string;
      }
      case ETransactionType.EXPENSE: {
        return getExpensesColors().string;
      }
      case "DIFFERENCE": {
        return isPositiveDifference
          ? getBalanceColors().string.positive
          : getBalanceColors().string.negative;
      }

      default: {
        return {};
      }
    }
  }, [kind, isPositiveDifference]);

  const getIcon = useMemo(() => {
    switch (kind) {
      case ETransactionType.INCOME: {
        return (
          <MdArrowDownward
            size={ICON_SIZES.XS}
            style={{
              color: getIncomeColors().string.background,
            }}
          />
        );
      }
      case ETransactionType.EXPENSE: {
        return (
          <MdOutlineArrowUpward
            size={ICON_SIZES.XS}
            style={{
              color: getExpensesColors().string.background,
            }}
          />
        );
      }
      case "DIFFERENCE": {
        return (
          <MdOutlineSavings
            size={ICON_SIZES.XS}
            style={{
              color: isPositiveDifference
                ? getBalanceColors().string.positive.background
                : getBalanceColors().string.negative.background,
            }}
          />
        );
      }
      default: {
        return <></>;
      }
    }
  }, [kind, isPositiveDifference]);

  return (
    <div
      className={clsx([
        "flex",
        "flex-row",
        "items-center",
        "justify-end",
        "gap-1",
        "text-xs",
        "md:text-base",
        "font-medium",
      ])}
    >
      {showIcon && <>{getIcon}</>}
      <div
        className={clsx([
          "font-medium",
          "text-xs",
          "md:text-sm",
          "rounded",
          {
            "px-2": kind !== "NONE",
          },
          "shrink-0",
        ])}
        style={getBackgroundStyles}
      >
        {enableAnimations ? (
          <CountUp
            end={value}
            duration={COUNT_UP_ANIMATION_DURATION}
            separator=","
            decimals={hasDecimals(value) ? 2 : 0}
            delay={0.1}
            preserveValue
            prefix={currencySymbol}
          />
        ) : (
          <div>
            {hasDecimals(value)
              ? formatCurrency({
                  value: value.toFixed(2),
                  currency: currencySymbol,
                })
              : formatCurrency({ value: value, currency: currencySymbol })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyBadge;
